<script>
import CancelFlowModal from "../../components/modals/modal-cancel-flow"
import SettingFaq from './setting-faq.vue';
import { PLAN_DESCRIPTION, PLAN_FEATURES, PLAN_PUBLIC_NAMES, PLAN_SKU_TYPES, PREFLIGHT_PRICES } from '../../utils/constants';
import { formatDate, isEligibleForFirstTimeOffer } from '../../utils/helpers.js';

const { FREE_PLAN, SUSPENDED_PLAN, TRIAL_PLAN, PREMIUM_PLAN } = PLAN_SKU_TYPES
const { FREE, STARTER, STANDARD, PREMIUM, SUSPENDED } = PLAN_PUBLIC_NAMES
const { FREE_DESCRIPTION, STARTER_DESCRIPTION, STANDARD_DESCRIPTION, STANDARD_1_DESCRIPTION, STANDARD_2_DESCRIPTION, PREMIUM_DESCRIPTION, PREMIUM_1_DESCRIPTION, PREMIUM_2_DESCRIPTION } = PLAN_DESCRIPTION
const { FREE_FEATURES, STARTER_FEATURES, STANDARD_FEATURES, STANDARD_1_FEATURES, STANDARD_2_FEATURES, PREMIUM_FEATURES, PREMIUM_1_FEATURES, PREMIUM_2_FEATURES } = PLAN_FEATURES
const FOREVER_FREE = "Forever Free"
const PREFLIGHT_MONTHLY_PRICE = 46;
const PREFLIGHT_ANNUAL_MONTHLY_PRICE = 33;

export default {
  name: 'pricePlan',
  components: {
    CancelFlowModal,
    SettingFaq
  },
  data() {
    return {
      selectedPlan: _.get(this.$A, 'Auth.account.subscriptionState.sku'),
      currentSubscription: this.emptySubscription(),
      plans: [],
      billingCycle: 'annual',
      preflightPlans: {},
      showPreflightAddon: false,
      FOREVER_FREE,
      SUSPENDED,
    }
  },
  async mounted() {
    await this.$A.GetCurAccountData();
    await this.getCurrentSub();
  },
  methods: {
    cancelCurrentSub: async function (data) {
      let newSub = await this.$A.BillingService.CancelSubscription();
      // null = network/server failure (CancelSubscription suppresses alerts)
      if (!newSub || (newSub.success !== undefined && !newSub.success)) {
        this.$A.AlertUser("We couldn't cancel your subscription. Please try again.", "warning");
        return;
      }
      await this.getCurrentSub();
      await this.$A.GetCurAccountData();
      this.$root.$emit('nav::reload');

      let slackTextMessage = `Cancellation Reason(s): ${data.reason}.\nFeatures Requested: ${data.features}\nAdditional Feedback: ${data.feedback}\nRetention Offers Declined: ${data.offersDeclined && data.offersDeclined.length ? data.offersDeclined.join(', ') : 'none shown'}`
      await this.$A.FeedbackService.CreateSlackMessage({
        slackChannel: "ns_purchase_events",
        subject: "Subscription Cancellation Notice",
        text: slackTextMessage
      })
    },
    offerAccepted: async function () {
      await this.getCurrentSub();
      await this.$A.GetCurAccountData();
      this.$root.$emit('nav::reload');
    },
    updatePlanDetails: function (plan) {
      if (plan.publicName === FREE) {
        plan.publicName = FOREVER_FREE;
        plan.planDescription = FREE_DESCRIPTION;
        plan.features = FREE_FEATURES;
      } else if (plan.publicName === STARTER) {
        plan.planDescription = STARTER_DESCRIPTION;
        plan.features = STARTER_FEATURES;
      } else if (plan.publicName === STANDARD) {
        // Check if this is Standard-1 or Standard-2 based on SKU
        const isStandard1 = plan.sku.includes('standard-1');
        const isStandard2 = plan.sku.includes('standard-2');

        if (isStandard1) {
          plan.planDescription = STANDARD_1_DESCRIPTION;
          plan.features = STANDARD_1_FEATURES;
        } else if (isStandard2) {
          plan.planDescription = STANDARD_2_DESCRIPTION;
          plan.features = STANDARD_2_FEATURES;
        } else {
          // Fallback to generic Standard description for older plans
          plan.planDescription = STANDARD_DESCRIPTION;
          plan.features = STANDARD_FEATURES;
        }
      } else if (plan.publicName === PREMIUM) {
        // Check if this is Premium-1 or Premium-2 based on SKU
        const isPremium1 = plan.sku.includes('premium-1');
        const isPremium2 = plan.sku.includes('premium-2');

        if (isPremium1) {
          plan.planDescription = PREMIUM_1_DESCRIPTION;
          plan.features = PREMIUM_1_FEATURES;
        } else if (isPremium2) {
          plan.planDescription = PREMIUM_2_DESCRIPTION;
          plan.features = PREMIUM_2_FEATURES;
        } else {
          // Fallback to generic Premium description for older plans
          plan.planDescription = PREMIUM_DESCRIPTION;
          plan.features = PREMIUM_FEATURES;
        }
      }
    },
    getCurrentSub: async function () {
      this.currentSubscription = await this.$A.BillingService.GetCurrentSubscription() || this.emptySubscription();
      const planKey = this.currentSubscription.plan.sku.replace("-annual", "");
      if (this.currentSubscription.plan.cycleMonths === 12 && this.currentSubscription.planSku !== SUSPENDED_PLAN) {
        this.billingCycle = 'annual';
      } else if (!this.isFreeUser) {
        this.billingCycle = 'monthly';
      }
      if (!_.isEmpty(this.currentSubscription.additionalSkus)) this.$set(this.preflightPlans, planKey, true)
      _.set(this, 'currentSubscription.plan.isUpgrade', false);
      _.set(this, 'currentSubscription.plan.isDownGrade', false);
      let plans = [];
      plans.push(this.currentSubscription.plan);

      this.currentSubscription.downGradeOptions.forEach(function (i) {
        i.isDownGrade = true;
        i.isUpgrade = false;
        plans.push(i)
      });
      this.currentSubscription.upgradeOptions.forEach(function (i) {
        i.isUpgrade = true;
        i.isDownGrade = false;
        plans.push(i)
      });

      plans.map((plan) => this.updatePlanDetails(plan))
      this.plans = this.updatedPlans(plans).sort(function (a, b) {
        return a.unitPrice - b.unitPrice;
      });
      await this.$A.UpdateCurrentSubscriptionState(_.get(this.currentSubscription, 'subscriptionState'));
    },
    updatedPlans: function (plans) {
      const filteredPlans = plans.filter(plan => plan.cycleMonths === 1 || plan.cycleMonths === 12);
      const groupedPlans = filteredPlans.reduce((groups, plan) => {
        const { publicName } = plan;
        if (!groups[publicName]) {
          groups[publicName] = [];
        }
        groups[publicName].push(plan);
        return groups;
      }, {});

      const updatedPlans = Object.values(groupedPlans).map(group => {
        const planWithCycleOne = group.find(plan => plan.cycleMonths === 1);
        const selectedPlan = planWithCycleOne || group.find(plan => plan.cycleMonths === 12);

        if (selectedPlan && selectedPlan.cycleMonths === 12) {
          selectedPlan.publicCyclePrice = selectedPlan.monthlyCostWhenPaidAnnually;
        }

        return selectedPlan;
      });
      return updatedPlans.flat()
    },
    emptySubscription: function () {
      return {
        planSku: _.get(this, '$A.Auth.account.subscriptionState.sku', ''),
        plan: {
          publicCyclePrice: ""
        },
        subscriptionState: {
          remainingProofsOnPlan: 0,
        },
        downGradeOptions: [],
        upgradeOptions: []
      };
    },
    showCurrentUser(plan) {
      let isCurrentPlan = false;
      if (this.isFreeUser && plan.publicName === FOREVER_FREE) isCurrentPlan = true;
      else if (this.currentSubscription.planSku.includes(plan.sku)) isCurrentPlan = true;
      return isCurrentPlan;
    },
    isAnnualFor(plan) {
      return plan.cycleMonths === 12 || this.billingCycle === 'annual';
    },
    preflightPrice(plan) {
      let preflightPrice = PREFLIGHT_MONTHLY_PRICE;
      if (this.isAnnualFor(plan)) preflightPrice = PREFLIGHT_ANNUAL_MONTHLY_PRICE;
      return preflightPrice
    },
    togglePreflightAddOn(planSku) {
      this.$set(this.preflightPlans, planSku, !this.preflightPlans[planSku]);
    },
    getPlanPrice(plan, withCoupon = true) {
      const isStandard1 = plan.sku.includes('standard-1');

      // Handle Standard-1 plans with fixed $18/user/month pricing
      if (isStandard1) {
        let price = 18; // Standard-1 is $18/user/month

        if (withCoupon && this.shouldShowCouponAppliedPlan) {
          price /= 2;
        }

        return `$${price}`;
      }

      let planValue = plan.publicCyclePrice.split('/')[0];

      if (this.isAnnualFor(plan) && plan.monthlyCostWhenPaidAnnually != null) {
        planValue = plan.monthlyCostWhenPaidAnnually.split('/')[0];
      }

      const symbol = planValue.trim().charAt(0) === '$' ? '$' : '';
      let numericValue = parseFloat(planValue.replace(/[^0-9.]/g, ''));

      if (withCoupon && this.shouldShowCouponAppliedPlan) {
        numericValue /= 2;
      }

      return `${symbol}${numericValue}`;
    },
    getTieredPricingDisplay(plan, withCoupon = true) {
      // Handle tiered pricing display for tiered pricing model
      const isPremium1 = plan.sku.includes('premium-1');
      const isPremium2 = plan.sku.includes('premium-2');
      const isStandard2 = plan.sku.includes('standard-2');
      const couponApplies = withCoupon && this.shouldShowCouponAppliedPlan && !this.showCurrentUser(plan);

      if (isPremium1) {
        // Premium-1: $165/month base price with 5 included users
        if (this.isAnnualFor(plan)) {
          return couponApplies
            ? '$990' // $1,980 / 2 with coupon ($165 * 12)
            : '$1,980';
        }
        return couponApplies
          ? '$83' // $165 / 2 with coupon (rounded)
          : '$165';
      } else if (isPremium2) {
        // Premium-2: $249/month base price with 5 included users
        if (this.isAnnualFor(plan)) {
          return couponApplies
            ? '$1,494' // $2,988 / 2 with coupon ($249 * 12)
            : '$2,988';
        }
        return couponApplies
          ? '$125' // $249 / 2 with coupon (rounded)
          : '$249';
      } else if (isStandard2) {
        if (this.isAnnualFor(plan)) {
          return couponApplies
            ? '$274' // $547 / 2 with coupon (rounded)
            : '$547';
        }
        return couponApplies
          ? '$29' // $57 / 2 with coupon (rounded)
          : '$57';
      }
      return this.getPlanPrice(plan, withCoupon);
    },
    getAdditionalUserPrice(plan) {
      // Get the per-additional-user price for tiered plans
      const isPremium1 = plan.sku.includes('premium-1');
      const isPremium2 = plan.sku.includes('premium-2');
      const isStandard2 = plan.sku.includes('standard-2');

      if (isPremium1) {
        // Premium-1: $33/user/month for additional users
        return this.isAnnualFor(plan) ? '$396' : '$33';
      } else if (isPremium2) {
        // Premium-2: $29/user/month for additional users
        return this.isAnnualFor(plan) ? '$348' : '$29';
      } else if (isStandard2) {
        // Standard-2: $19/user/month for additional users
        return this.isAnnualFor(plan) ? '$228' : '$19';
      }
      return '';
    },
    showTieredPricing(plan) {
      // Show tiered pricing for all tiered plans (both -1 and -2 variants)
      const isPremium1 = plan.sku.includes('premium-1');
      const isPremium2 = plan.sku.includes('premium-2');
      const isStandard2 = plan.sku.includes('standard-2');
      return isPremium1 || isPremium2 || isStandard2;
    },
    getIncludedUsers(plan) {
      // Return included users for tiered plans
      const isPremium1 = plan.sku.includes('premium-1');
      const isPremium2 = plan.sku.includes('premium-2');
      const isStandard2 = plan.sku.includes('standard-2');

      if (isPremium1 || isPremium2) {
        return 5; // Both Premium-1 and Premium-2 include 5 users
      } else if (isStandard2) {
        return 3; // Standard-2 includes 3 users
      }
      return 1;
    },
    priceFor(plan) {
      return this.showTieredPricing(plan) ? this.getTieredPricingDisplay(plan) : this.getPlanPrice(plan);
    },
    priceBeforeCoupon(plan) {
      return this.showTieredPricing(plan) ? this.getTieredPricingDisplay(plan, false) : this.getPlanPrice(plan, false);
    },
    couponApplies(plan) {
      return this.shouldShowCouponAppliedPlan && !this.showCurrentUser(plan) && plan.publicName !== FOREVER_FREE && plan.publicName !== SUSPENDED;
    },
    priceUnit(plan) {
      if (plan.publicName === FOREVER_FREE || plan.publicName === SUSPENDED) return '';
      if (this.showTieredPricing(plan)) return this.isAnnualFor(plan) ? '/year' : '/month';
      return '/month per user';
    },
    priceNote(plan) {
      if (plan.publicName === FOREVER_FREE || plan.publicName === SUSPENDED) return '';
      if (this.showTieredPricing(plan) && this.getAdditionalUserPrice(plan)) {
        return `Includes ${this.getIncludedUsers(plan)} users, then ${this.getAdditionalUserPrice(plan)}/user/${this.isAnnualFor(plan) ? 'year' : 'month'}`;
      }
      if (this.isAnnualFor(plan)) return 'Billed annually';
      return '';
    },
    isRecommended(plan) {
      return this.recommendedSku && plan.sku === this.recommendedSku;
    },
    showPlanCta(plan) {
      if (this.showCurrentUser(plan)) return true;
      return plan.publicName !== FOREVER_FREE && plan.publicName !== SUSPENDED;
    },
    redirectToStripe(url) {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async handlePlanClick(plan) {
      if (plan.publicName === FOREVER_FREE || plan.publicName === SUSPENDED) return;
      const isAnnual = plan.cycleMonths === 1 && this.billingCycle === 'annual';
      const mainSku = isAnnual ? `${plan.sku}-annual` : plan.sku;
      const isPreflightSelected = this.preflightPlans[plan.sku] || false;
      let additionalSkus = {};

      if (isPreflightSelected) {
        additionalSkus[`preflights${isAnnual ? '-annual' : ''}`] = 1;
      }

      const checkoutSessionRequest = {
        stripeCheckoutSessionRequest: {
          "MainSku": mainSku,
          "AdditionalSkus": additionalSkus
        }
      }

      const result = await this.$A.BillingService.CreateCheckoutSession(checkoutSessionRequest);
      result?.url && this.redirectToStripe(result.url)
    },
    async redirectCustomerPortal() {
      const result = await this.$A.BillingService.CreateCustomerPortalSession()
      result?.url && this.redirectToStripe(result.url)
    },
    getButtonText(plan) {
      if (this.showCurrentUser(plan)) return this.isFreeUser ? 'Current plan' : 'Update plan';
      if (plan.isDownGrade) return `Downgrade to ${plan.publicName}`;
      return `Upgrade to ${plan.publicName}`;
    }
  },
  computed: {
    isFreeUser() {
      return this.currentSubscription.planSku === FREE_PLAN || this.currentSubscription.planSku === SUSPENDED_PLAN || this.currentSubscription.planSku === TRIAL_PLAN;
    },
    shouldShowCouponAppliedPlan() {
      return  isEligibleForFirstTimeOffer(this.$A.Auth, this.isFreeUser);
    },
    pendingCancel() {
      return !this.isFreeUser && !!this.currentSubscription.cancelAtPeriodEnd;
    },
    cancelDateDisplay() {
      return formatDate(this.currentSubscription.currentPeriodEnd);
    },
    // Retention offers for the cancel stepper. Premium -> Standard, Standard
    // -> Starter (downgrade + 3 free months of the target); Starter -> 3 free
    // months of Starter itself; anything else -> no offers, straight to the
    // feedback step. The free-months offer is monthly-only: a repeating
    // 3-month Stripe coupon never reaches an annual subscriber's next invoice.
    retentionOffers() {
      const plan = this.currentSubscription.plan || {};
      const prefix = (this.currentSubscription.planSku || '').split('-')[0];
      const isMonthly = plan.cycleMonths === 1;

      if (prefix === 'starter') {
        return { downgrade: null, free: isMonthly ? { sku: plan.sku, name: plan.publicName } : null };
      }

      const targetName = prefix === 'premium' ? STANDARD : (prefix === 'standard' ? STARTER : null);
      if (!targetName) return { downgrade: null, free: null };

      const options = this.currentSubscription.downGradeOptions.filter(
        o => o.publicName === targetName && (o.cycleMonths === 1 || o.cycleMonths === 12)
      );
      const target = options.find(o => o.cycleMonths === plan.cycleMonths) || options[0];
      if (!target) return { downgrade: null, free: null };

      return {
        downgrade: {
          sku: target.sku,
          name: target.publicName,
          price: this.priceFor(target),
          unit: this.priceUnit(target),
          note: this.priceNote(target),
        },
        free: isMonthly ? { sku: target.sku, name: target.publicName } : null,
      };
    },
    nonPremiumPlans() {
      // Free/suspended plans are summarized in the "Your subscription" strip, not sold as cards
      return this.plans.filter(p => p.publicName !== PREMIUM && p.publicName !== FOREVER_FREE && p.publicName !== SUSPENDED);
    },
    premiumPlan() {
      return this.plans.find(p => p.publicName === PREMIUM);
    },
    recommendedSku() {
      const upgrades = this.nonPremiumPlans.filter(p => p.isUpgrade && p.publicName !== FOREVER_FREE && p.publicName !== SUSPENDED);
      if (!upgrades.length) return null;
      const standard = upgrades.find(p => p.sku.includes('standard'));
      return (standard || upgrades[upgrades.length - 1]).sku;
    },
    subscriptionDetails() {
      const { plan, nextInvoiceTotal, additionalSkus, subscriptionState } = this.currentSubscription;
      const { userCount = 1 } = subscriptionState;
      if (!plan || plan.publicName === FREE) return {};

      const { publicName, cycleMonths, publicCyclePrice, monthlyCostWhenPaidAnnually, sku } = plan;
      const users = (publicName === PREMIUM && userCount < 5) ? 5 : userCount;
      const preflightAddon = additionalSkus && Object.keys(PREFLIGHT_PRICES).find(sku => additionalSkus[sku]);
      const addons = preflightAddon ? PREFLIGHT_PRICES[preflightAddon] : 0;
      const removeDollarSign = value => parseFloat(value.replace('$', '').trim());

      const monthlyCostAnnually = (monthlyCostWhenPaidAnnually != null) ? removeDollarSign(monthlyCostWhenPaidAnnually.split('/')[0]) : "";
      const cyclePrice = removeDollarSign(publicCyclePrice.split('/')[0]);

      const isAnnual = cycleMonths === 12;

      // Calculate tiered pricing for new pricing model
      let calculatedCost = 0;
      let costDisplay = '';

      if (this.showTieredPricing(plan)) {
        const includedUsers = this.getIncludedUsers(plan);
        const additionalUsers = Math.max(0, userCount - includedUsers);

        const isPremium2 = sku.includes('premium-2');
        const isStandard2 = sku.includes('standard-2');

        if (isPremium2) {
          const basePrice = isAnnual ? 2390.40 : 249;
          const perUserPrice = isAnnual ? 278.40 : 29;
          calculatedCost = basePrice + (additionalUsers * perUserPrice);

          if (additionalUsers > 0) {
            costDisplay = `$${basePrice.toFixed(isAnnual ? 2 : 0)} (${includedUsers} users) + $${(additionalUsers * perUserPrice).toFixed(isAnnual ? 2 : 0)} (${additionalUsers} additional)`;
          } else {
            costDisplay = `$${basePrice.toFixed(isAnnual ? 2 : 0)}`;
          }
        } else if (isStandard2) {
          const basePrice = isAnnual ? 547.20 : 57;
          const perUserPrice = isAnnual ? 182.40 : 19;
          calculatedCost = basePrice + (additionalUsers * perUserPrice);

          if (additionalUsers > 0) {
            costDisplay = `$${basePrice.toFixed(isAnnual ? 2 : 0)} (${includedUsers} users) + $${(additionalUsers * perUserPrice).toFixed(isAnnual ? 2 : 0)} (${additionalUsers} additional)`;
          } else {
            costDisplay = `$${basePrice.toFixed(isAnnual ? 2 : 0)}`;
          }
        }
      } else {
        calculatedCost = (isAnnual ? monthlyCostAnnually : cyclePrice) * users;
        costDisplay = `${users} × $${isAnnual ? monthlyCostAnnually : cyclePrice}/month`;
      }

      return {
        subscription: publicName,
        frequency: isAnnual ? 'Annual' : 'Monthly',
        costPerUser: isAnnual ? monthlyCostAnnually : cyclePrice,
        users,
        addons,
        nextInvoiceTotal,
        calculatedCost: calculatedCost.toFixed(2),
        costDisplay,
        showTieredBreakdown: this.showTieredPricing(plan)
      };
    },
  },
}
</script>

<template>
  <div class="billing-content">
    <div v-if="plans.length" class="billing-inner">
      <div class="billing-head">
        <div class="billing-head-text">
          <h1 class="billing-title">Plan &amp; Billing</h1>
          <p class="billing-subtitle">Pick the plan that fits your team. Change or cancel anytime.</p>
        </div>
        <div class="cycle-toggle" role="group" aria-label="Billing cycle">
          <button
            type="button"
            class="cycle-btn"
            :class="{ 'is-active': billingCycle === 'monthly' }"
            @click="billingCycle = 'monthly'"
          >Monthly</button>
          <button
            type="button"
            class="cycle-btn"
            :class="{ 'is-active': billingCycle === 'annual' }"
            @click="billingCycle = 'annual'"
          >Annual <span class="cycle-save">Save 20%</span></button>
        </div>
      </div>

      <!-- Current subscription summary (free users) -->
      <div v-if="isFreeUser" class="sub-card">
        <div class="sub-card-main">
          <div class="micro-label">Your subscription</div>
          <div class="sub-card-plan">
            {{ currentSubscription.plan.publicName || 'Forever Free' }}
            <span v-if="currentSubscription.plan.publicName === FOREVER_FREE" class="sub-pill">$0/month</span>
          </div>
          <div v-if="currentSubscription.plan.planDescription" class="sub-card-breakdown">{{ currentSubscription.plan.planDescription }}</div>
        </div>
      </div>

      <!-- Current subscription summary (paying users) -->
      <div v-else-if="subscriptionDetails.subscription" class="sub-card">
        <div class="sub-card-main">
          <div class="micro-label">Your subscription</div>
          <div class="sub-card-plan">
            {{ subscriptionDetails.subscription }}
            <span class="sub-pill">{{ subscriptionDetails.frequency }}</span>
            <span v-if="pendingCancel" class="sub-pill sub-pill--warn">Cancels on {{ cancelDateDisplay }}</span>
          </div>
          <div class="sub-card-breakdown">
            <span v-if="subscriptionDetails.showTieredBreakdown">{{ subscriptionDetails.costDisplay }}</span>
            <span v-else>{{ subscriptionDetails.users }} {{ subscriptionDetails.users === 1 ? 'user' : 'users' }} × ${{ subscriptionDetails.costPerUser }}/month</span>
            <span v-if="subscriptionDetails.addons"> · Preflights add-on ${{ subscriptionDetails.addons }}/month</span>
          </div>
        </div>
        <div class="sub-card-invoice">
          <div class="micro-label">Upcoming invoice</div>
          <div class="sub-invoice-amount">${{ subscriptionDetails.nextInvoiceTotal }}</div>
        </div>
        <div class="sub-card-actions">
          <button type="button" class="btn-secondary" @click="redirectCustomerPortal">Manage billing</button>
          <button v-if="!pendingCancel" type="button" class="btn-ghost" @click="$refs.cancelFlowModal.open()">Cancel plan</button>
        </div>
      </div>

      <!-- Plan cards -->
      <div class="plans-grid">
        <div
          v-for="plan in nonPremiumPlans"
          :key="plan.id"
          class="plan-card"
          :class="{ 'is-current': showCurrentUser(plan), 'is-recommended': isRecommended(plan) }"
        >
          <div v-if="showCurrentUser(plan)" class="plan-flag plan-flag--current">Current plan</div>
          <div v-else-if="isRecommended(plan)" class="plan-flag plan-flag--reco">Most popular</div>

          <div class="plan-name">{{ plan.publicName }}</div>
          <p class="plan-desc">{{ plan.planDescription }}</p>

          <div class="plan-price-row">
            <span v-if="couponApplies(plan)" class="price-was">{{ priceBeforeCoupon(plan) }}</span>
            <span class="price-now" :class="{ 'price-now--deal': couponApplies(plan) }">{{ priceFor(plan) }}</span>
            <span class="price-unit">{{ priceUnit(plan) }}</span>
          </div>
          <div class="plan-price-note">
            <span v-if="priceNote(plan)">{{ priceNote(plan) }}</span>
            <span v-if="couponApplies(plan)" class="deal-pill">50% off applied</span>
          </div>

          <button
            v-if="showPlanCta(plan)"
            type="button"
            class="plan-cta"
            :class="showCurrentUser(plan) ? 'plan-cta--current' : (isRecommended(plan) ? 'plan-cta--primary' : 'plan-cta--outline')"
            @click="handlePlanClick(plan)"
          >{{ getButtonText(plan) }}</button>

          <ul class="plan-features">
            <li v-for="feature in plan.features" :key="feature">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{{ feature }}</span>
            </li>
          </ul>

          <div v-if="showPreflightAddon && plan.publicName !== FOREVER_FREE && plan.publicName !== SUSPENDED" class="addon-block">
            <label class="addon-label">
              <input type="checkbox" class="addon-checkbox" :checked="preflightPlans[plan.sku]"
                @change="togglePreflightAddOn(plan.sku)">
              <span class="addon-box" aria-hidden="true"></span>
              <span class="addon-text">
                <span class="addon-title-row">
                  <span class="addon-title">Preflights add-on</span>
                  <span class="addon-price">${{ preflightPrice(plan) }}/month</span>
                </span>
                <span class="addon-desc">Turn customer artwork into print-ready PDFs in seconds.</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- Premium spotlight -->
      <div v-if="premiumPlan" class="premium-card" :class="{ 'is-current': showCurrentUser(premiumPlan) }">
        <div v-if="showCurrentUser(premiumPlan)" class="plan-flag plan-flag--current">Current plan</div>
        <span v-else class="premium-tier-badge">Top tier</span>

        <div class="premium-grid">
          <div class="premium-left">
            <div class="plan-name">{{ premiumPlan.publicName }}</div>
            <p class="plan-desc">{{ premiumPlan.planDescription }}</p>

            <div class="plan-price-row">
              <span v-if="couponApplies(premiumPlan)" class="price-was">{{ priceBeforeCoupon(premiumPlan) }}</span>
              <span class="price-now" :class="{ 'price-now--deal': couponApplies(premiumPlan) }">{{ priceFor(premiumPlan) }}</span>
              <span class="price-unit">{{ priceUnit(premiumPlan) }}</span>
            </div>
            <div class="plan-price-note">
              <span v-if="priceNote(premiumPlan)">{{ priceNote(premiumPlan) }}</span>
              <span v-if="couponApplies(premiumPlan)" class="deal-pill">50% off applied</span>
            </div>

            <button
              type="button"
              class="plan-cta"
              :class="showCurrentUser(premiumPlan) ? 'plan-cta--current' : 'plan-cta--primary'"
              @click="handlePlanClick(premiumPlan)"
            >{{ getButtonText(premiumPlan) }}</button>
          </div>

          <ul class="plan-features premium-features">
            <li v-for="feature in premiumPlan.features" :key="feature">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{{ feature }}</span>
            </li>
          </ul>
        </div>
      </div>

      <p class="billing-reassurance">Prices in USD. You can change or cancel your plan at any time.</p>

      <div v-if="isFreeUser" class="faq-card">
        <SettingFaq />
      </div>
    </div>

    <CancelFlowModal
      ref="cancelFlowModal"
      :current-plan-name="currentSubscription.plan.publicName"
      :period-end="currentSubscription.currentPeriodEnd"
      :downgrade-offer="retentionOffers.downgrade"
      :free-offer="retentionOffers.free"
      v-on:confirmed="cancelCurrentSub"
      v-on:offer-accepted="offerAccepted" />
  </div>
</template>

<style scoped>
.billing-content {
  min-height: calc(100vh - 64px);
}
.billing-inner {
  max-width: 1160px;
  margin: 0 auto;
  padding: 32px 24px 64px;
}

/* Header row */
.billing-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}
.billing-title {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.billing-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

/* Billing cycle segmented toggle */
.cycle-toggle {
  display: inline-flex;
  align-items: center;
  padding: 3px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04);
}
.cycle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.cycle-btn:hover {
  color: #111827;
}
.cycle-btn.is-active {
  background: #019256;
  color: #fff;
}
.cycle-save {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: rgba(1, 146, 86, 0.1);
  color: #017a48;
}
.cycle-btn.is-active .cycle-save {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

/* Shared micro label */
.micro-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* Current subscription summary */
.sub-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px 32px;
  padding: 18px 20px;
  margin-bottom: 24px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.sub-card-main {
  flex: 1;
  min-width: 220px;
}
.sub-card-plan {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}
.sub-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: rgba(1, 146, 86, 0.08);
  color: #017a48;
}
.sub-pill--warn {
  background: rgba(212, 77, 104, 0.1);
  color: #b93a55;
}
.sub-card-breakdown {
  margin-top: 3px;
  font-size: 13px;
  color: #6b7280;
}
.sub-card-invoice {
  min-width: 140px;
}
.sub-invoice-amount {
  margin-top: 4px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}
.sub-card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 12px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.btn-ghost:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #111827;
}

/* Plan cards */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 22px 20px 20px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.plan-card.is-recommended {
  border-color: rgba(1, 146, 86, 0.45);
  box-shadow: 0 0 0 1px rgba(1, 146, 86, 0.25), 0 8px 24px -16px rgba(1, 146, 86, 0.25);
}
.plan-flag {
  position: absolute;
  top: -11px;
  left: 16px;
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.plan-flag--current {
  background: #eef2f5;
  border: 1px solid rgba(15, 23, 42, 0.1);
  color: #374151;
}
.plan-flag--reco {
  background: #019256;
  color: #fff;
}
.plan-name {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}
.plan-desc {
  margin: 3px 0 14px;
  font-size: 12.5px;
  color: #6b7280;
  min-height: 34px;
}
.plan-price-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}
.price-was {
  font-size: 15px;
  font-weight: 600;
  color: #9ca3af;
  text-decoration: line-through;
}
.price-now {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
  color: #111827;
}
.price-now--deal {
  color: #017a48;
}
.price-unit {
  font-size: 13px;
  color: #6b7280;
}
.plan-price-note {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
  min-height: 18px;
  font-size: 12px;
  color: #6b7280;
}
.deal-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  background: rgba(1, 146, 86, 0.1);
  color: #017a48;
}

/* CTAs */
.plan-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 38px;
  margin: 16px 0;
  padding: 0 16px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.plan-cta--primary {
  background: #019256;
  border: 1px solid #019256;
  color: #fff;
}
.plan-cta--primary:hover {
  background: #017a48;
  border-color: #017a48;
}
.plan-cta--outline {
  background: #fff;
  border: 1px solid rgba(1, 146, 86, 0.55);
  color: #017a48;
}
.plan-cta--outline:hover {
  background: rgba(1, 146, 86, 0.06);
}
.plan-cta--current {
  background: #eef2f5;
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #374151;
}
.plan-cta--current:hover {
  background: #e4e9ee;
}

/* Feature lists */
.plan-features {
  list-style: none;
  margin: 0;
  padding: 14px 0 0;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.plan-features li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.4;
}
.plan-features svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: #019256;
}

/* Preflight add-on */
.addon-block {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.addon-label {
  display: flex;
  gap: 10px;
  margin: 0;
  cursor: pointer;
}
.addon-checkbox {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.addon-box {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  position: relative;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.addon-checkbox:checked + .addon-box {
  background: #019256;
  border-color: #019256;
}
.addon-checkbox:checked + .addon-box::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 5px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.addon-checkbox:focus-visible + .addon-box {
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.addon-text {
  flex: 1;
  min-width: 0;
}
.addon-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.addon-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}
.addon-price {
  font-size: 12.5px;
  color: #6b7280;
}
.addon-desc {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
}

/* Premium spotlight */
.premium-card {
  position: relative;
  padding: 24px;
  margin-bottom: 16px;
  background: linear-gradient(180deg, rgba(1, 146, 86, 0.035), rgba(1, 146, 86, 0)) #fff;
  border: 1px solid rgba(1, 146, 86, 0.25);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.premium-tier-badge {
  position: absolute;
  top: 18px;
  right: 20px;
  padding: 3px 10px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #017a48;
  background: rgba(1, 146, 86, 0.1);
  border-radius: 999px;
}
.premium-grid {
  display: grid;
  grid-template-columns: minmax(260px, 320px) 1fr;
  gap: 40px;
  align-items: start;
}
.premium-left .plan-cta {
  margin-bottom: 0;
}
.premium-features {
  border-top: 0;
  padding-top: 4px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 28px;
  row-gap: 10px;
}

/* Reassurance line */
.billing-reassurance {
  margin: 0 0 32px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
}

/* FAQ (free users) */
.faq-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  overflow: hidden;
}
.faq-card ::v-deep .faq-container {
  padding: 40px 48px;
}

/* Responsive */
@media (max-width: 991px) {
  .premium-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .premium-features {
    grid-template-columns: 1fr;
  }
  .sub-card-actions {
    width: 100%;
  }
}
@media (max-width: 575px) {
  .billing-inner {
    padding: 24px 16px 48px;
  }
  .plans-grid {
    grid-template-columns: 1fr;
  }
  .faq-card ::v-deep .faq-container {
    padding: 24px;
  }
  .btn-secondary {
    flex: 1;
  }
}
</style>
